import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useSidebarPageLayoutActions} from '@github-ui/layout-primitives/SidebarPageLayout'
import type {SidebarLink} from '@github-ui/saved-views'
import {useAnalytics} from '@github-ui/use-analytics'
import {PlusIcon, StackIcon} from '@primer/octicons-react'
import {ActionList, IconButton, NavList, Truncate} from '@primer/react'
import {useId} from 'react'

import {getHandlerNavItemClick} from '../utils/sidebar-link'
import styles from './ReposShortcutSidebar.module.css'
import {SidebarLinkIcon} from './SidebarLinkIcon'

interface ReposShortcutSidebarProps {
  shortcuts: SidebarLink[]
  onSelectLink: (linkItem: SidebarLink) => void
  selectedLink: string
  onCreateView: () => void
}

/**
 * Renders the "Views" (saved views) group as a `CollapsibleNavList.Group`
 * child — a fragment, not its own list root. Exported so a parent can compose
 * it into a shared `CollapsibleNavList` alongside the repository-type links.
 * Primer's `NavList.Group` renders its own leading divider, so no explicit
 * divider (or padding hack) is needed to separate it from the section above.
 */
export function ReposShortcutViewsGroup({
  shortcuts,
  selectedLink,
  onSelectLink,
  onCreateView,
}: ReposShortcutSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {closeDialog} = useSidebarPageLayoutActions()

  const handleOnCreateView = (closePopover?: () => void) => {
    closeDialog()
    closePopover?.()
    onCreateView()
  }

  const items =
    shortcuts.length === 0 ? (
      <li className={styles.emptyStateItem}>
        <span className="text-small">No saved views</span>
      </li>
    ) : (
      shortcuts.map(linkItem => (
        <CollapsibleNavList.Item
          aria-current={selectedLink === linkItem.id ? 'page' : undefined}
          key={linkItem.id}
          href={`?q=${linkItem.query}`}
          onClick={getHandlerNavItemClick({
            linkItem,
            sendAnalyticsEvent,
            onSelectLink: selectedShortcut => {
              closeDialog()
              onSelectLink(selectedShortcut)
            },
          })}
        >
          <CollapsibleNavList.LeadingVisual>
            <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
          </CollapsibleNavList.LeadingVisual>
          <Truncate title={linkItem.name} maxWidth="min(100%, 32ch)">
            {linkItem.name}
          </Truncate>
        </CollapsibleNavList.Item>
      ))
    )

  return (
    <CollapsibleNavList.Group
      title="Views"
      icon={StackIcon}
      expandedHeading
      showDivider
      popoverSize="medium"
      headingLevel="h2"
      trailingAction={({close}) => (
        <ActionList.GroupHeading.TrailingAction
          icon={PlusIcon}
          label="Create view"
          tooltipDirection="e"
          onClick={() => {
            handleOnCreateView(close)
          }}
        />
      )}
    >
      {items}
    </CollapsibleNavList.Group>
  )
}

/**
 * Legacy standalone variant retained for existing consumers.
 * `ReposShortcutViewsGroup` is used by the repository finder layout.
 */
export function ReposShortcutSidebar({shortcuts, selectedLink, onSelectLink, onCreateView}: ReposShortcutSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const createViewButtonId = useId()

  return (
    <div>
      <div className={styles.headerRow} id={createViewButtonId}>
        <span className={styles.headerRowText}>Views</span>
        <IconButton icon={PlusIcon} size="small" variant="invisible" onClick={onCreateView} aria-label="Create view" />
      </div>
      {shortcuts.length === 0 ? (
        <div className={styles.noSavedViews}>No saved views</div>
      ) : (
        <NavList aria-labelledby={createViewButtonId} className={styles.savedViewsList}>
          {shortcuts.map(linkItem => (
            <NavList.Item
              aria-current={selectedLink === linkItem.id && 'page'}
              key={linkItem.id}
              href={`?q=${linkItem.query}`}
              onClick={getHandlerNavItemClick({onSelectLink, sendAnalyticsEvent, linkItem})}
            >
              <NavList.LeadingVisual>
                <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
              </NavList.LeadingVisual>
              <Truncate title={linkItem.name} maxWidth="100%">
                {linkItem.name}
              </Truncate>
            </NavList.Item>
          ))}
        </NavList>
      )}
    </div>
  )
}
